import React, { useState } from "react";
import { Modal, DatePicker, Button, Tooltip } from "antd";
import { 
  FileExcelOutlined, 
  FilePdfOutlined, 
  CalendarOutlined,
  CloseOutlined 
} from "@ant-design/icons";
import { NotificationManager } from "react-notifications";
import { CSVLink } from "react-csv";
import moment from "moment";

const { RangePicker } = DatePicker;

const styles = {
  overlay: {
    background: 'linear-gradient(135deg, #ff6700 0%, #e65c00 60%, #cc5200 100%)',
    borderRadius: '16px 16px 0 0',
    padding: '20px 24px',
    margin: '-24px -24px 20px -24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    overflow: 'hidden',
  },
  overlayBefore: {
    position: 'absolute',
    top: '-50%', right: '-5%',
    width: 200, height: 200,
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.08)',
    pointerEvents: 'none',
  },
  headerLeft: {
    display: 'flex', alignItems: 'center', gap: 12, zIndex: 1,
  },
  iconBox: {
    width: 40, height: 40,
    borderRadius: 10,
    background: 'rgba(255,255,255,0.2)',
    backdropFilter: 'blur(4px)',
    border: '1px solid rgba(255,255,255,0.3)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 18, color: '#fff',
  },
  headerTitle: { color: '#fff', fontSize: 16, fontWeight: 700, margin: 0 },
  headerSub: { color: 'rgba(255,255,255,0.8)', fontSize: 12, margin: 0, marginTop: 2 },
  closeBtn: {
    color: '#fff', cursor: 'pointer', fontSize: 16, zIndex: 1,
    padding: 4, borderRadius: 4, transition: 'background 0.2s',
  },
  formBody: {
    padding: '10px 0',
  },
  label: {
    fontSize: 12, fontWeight: 600, color: '#595959',
    textTransform: 'uppercase', letterSpacing: '0.4px',
    display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8,
  },
  pickerWrap: {
    background: '#fafafa', borderRadius: 12, padding: '16px',
    border: '1.5px solid #f0f0f0', marginBottom: 24,
  },
  actionBtns: {
    display: 'flex', gap: 12, justifyContent: 'flex-end',
  },
  btnCancel: {
    height: 42, borderRadius: 8, fontWeight: 600, color: '#595959',
    borderColor: '#d9d9d9', px: 20,
  },
  btnExport: {
    height: 42, borderRadius: 8, fontWeight: 600, background: '#ff6700',
    borderColor: '#ff6700', color: '#fff', display: 'flex', alignItems: 'center', gap: 8,
    padding: '0 24px', transition: 'all 0.2s',
  }
};

const ExportModal = ({ showModal, hideModal, dataList, onExportPdf }) => {
  const [dateRange, setDateRange] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  
  // Decide which mode we are in based on a prop or state passed from parent
  // Assuming we use the 'type' prop passed from parent: 'excel' or 'pdf'
  const exportType = showModal?.type || 'excel';

  const IconType = exportType === 'excel' ? FileExcelOutlined : FilePdfOutlined;
  const title = exportType === 'excel' ? 'Export as Excel' : 'Export as PDF';

  const handleFilterOperation = () => {
    if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      const start = dateRange[0].startOf('day').valueOf();
      const end = dateRange[1].endOf('day').valueOf();

      const filtered = dataList.filter(({ createdAt }) => {
        const itemDate = new Date(createdAt).getTime();
        return itemDate >= start && itemDate <= end;
      });
      
      setFilteredData(filtered);
      return filtered.length > 0;
    } else {
      NotificationManager.error("Please select a date range first");
      setFilteredData([]);
      return false;
    }
  };

  const handlePdfExport = () => {
    const isValid = handleFilterOperation();
    if (isValid && filteredData.length > 0) {
      if (onExportPdf) {
        // Send the filtered data back to parent for PDF generation
        onExportPdf(filteredData, {
          fromdate: dateRange[0].format("YYYY-MM-DD"),
          todate: dateRange[1].format("YYYY-MM-DD")
        });
      }
      hideModal(false);
    } else if (isValid && filteredData.length === 0) {
      NotificationManager.warning("No records found in this date range");
    }
  };

  return (
    <Modal
      open={!!showModal}
      closable={false}
      footer={null}
      centered
      onCancel={() => hideModal(false)}
      width={480}
      bodyStyle={{ padding: "24px", fontFamily: "'Inter', sans-serif" }}
    >
      <div style={styles.overlay}>
        <div style={styles.overlayBefore} />
        <div style={styles.headerLeft}>
          <div style={styles.iconBox}>
            <IconType />
          </div>
          <div>
            <p style={styles.headerTitle}>{title}</p>
            <p style={styles.headerSub}>Filter records by date range before exporting</p>
          </div>
        </div>
        <CloseOutlined 
          style={styles.closeBtn} 
          onClick={() => hideModal(false)}
        />
      </div>

      <div style={styles.formBody}>
        <div style={styles.label}>
          <CalendarOutlined style={{ color: '#ff6700' }} />
          Select Date Range
        </div>
        <div style={styles.pickerWrap}>
          <RangePicker
            style={{ width: '100%', height: 42, borderRadius: 8 }}
            format="DD MMM YYYY"
            onChange={(dates) => {
              setDateRange(dates);
              // Reset filtered data when dates change
              setFilteredData([]);
            }}
          />
        </div>

        <div style={styles.actionBtns}>
          <Button 
            style={styles.btnCancel} 
            onClick={() => hideModal(false)}
          >
            Cancel
          </Button>
          
          {exportType === 'excel' ? (
            <CSVLink
              data={filteredData}
              filename={`merchants_export_${moment().format('YYYYMMDD')}.csv`}
              style={{ textDecoration: 'none' }}
              onClick={(event, done) => {
                const isValid = handleFilterOperation();
                if (isValid) {
                  // Small timeout to allow state to update before download
                  setTimeout(() => done(), 100); 
                  hideModal(false);
                } else {
                  return false;
                }
              }}
            >
              <Button style={styles.btnExport} disabled={!dateRange || dateRange.length < 2}>
                <FileExcelOutlined /> Download Excel
              </Button>
            </CSVLink>
          ) : (
            <Button 
              style={styles.btnExport} 
              onClick={handlePdfExport}
              disabled={!dateRange || dateRange.length < 2}
            >
              <FilePdfOutlined /> Download PDF
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ExportModal;
